import { isValidChartListRecid } from '@/composables/isValidChartListRecid';
import { isValidChartListListNum } from '@/composables/isValidChartListListNum';
import { ChartList } from '@/interfaces/chartList';
import { JsonLayerChartList } from '@/interfaces/json-layer-responses/jsonLayerChartList';
import { stripHtmlFromString } from '@/plugins/vue-sanitize-html';

export function parseChartLists(rawChartLists: JsonLayerChartList[]): ChartList[] {
  if (!rawChartLists) {
    throw new Error(`Unable to parse ChartStyles - rawChartLists is falsy: ${typeof rawChartLists} ${rawChartLists}.`);
  }

  if (!Array.isArray(rawChartLists)) {
    throw new Error(`Unable to parse ChartStyles - rawChartLists is truthy but is not an array: ${typeof rawChartLists}.`);
  }

  const chartLists: ChartList[] = [];

  for (let i = 0; i < rawChartLists.length; i++) {
    const rawChartList = rawChartLists[i];

    if (!rawChartList) {
      window.sc3WorkbenchLogger.error(`Unable to parse ChartList at index ${i} - rawChartList is falsy: ${typeof rawChartList} ${rawChartList}.`);
      continue;
    }

    if (typeof rawChartList !== 'object') {
      window.sc3WorkbenchLogger.error(`Unable to parse ChartList at index ${i} - rawChartList is truthy but is not an object: ${typeof rawChartList} ${rawChartList}.`);
      continue;
    }

    if (!isValidChartListRecid(rawChartList.recID)) {
      window.sc3WorkbenchLogger.error(`Unable to parse ChartList at index ${i} - rawChartList.recID is not a valid ChartList recid: ${typeof rawChartList.recID} ${rawChartList.recID}.`);
      continue;
    }

    if (!isValidChartListListNum(rawChartList.listNum)) {
      window.sc3WorkbenchLogger.error(`Unable to parse ChartList at index ${i} with recid: ${rawChartList.recID} - rawChartList.listNum is not a valid ChartList listNum: ${typeof rawChartList.listNum} ${rawChartList.listNum}.`);
      continue;
    }

    if (typeof rawChartList.name !== 'string') {
      window.sc3WorkbenchLogger.error(`Unable to parse ChartList at index ${i} with recid: ${rawChartList.recID} - rawChartList.name is not a string: ${typeof rawChartList.name} ${rawChartList.name}.`);
      continue;
    }

    if (!rawChartList.name.trim()) {
      window.sc3WorkbenchLogger.error(`Unable to parse ChartList at index ${i} with recid: ${rawChartList.recID} - trimmed rawChartList.name is an empty string.`);
      continue;
    }

    if (typeof rawChartList.isReadOnly !== 'boolean') {
      window.sc3WorkbenchLogger.error(`Unable to parse ChartList at index ${i} with recid: ${rawChartList.recID} - rawChartList.isReadOnly is not a boolean: ${typeof rawChartList.isReadOnly}.`);
      continue;
    }

    let name = rawChartList.name.trim();

    if (rawChartList.isReadOnly) {
      name = `[READ-ONLY] ${name}`;
    }

    const listNum = Number(rawChartList.listNum);
    const htmlSanitizedName = stripHtmlFromString(name);

    chartLists.push({
      name,
      htmlSanitizedName,
      searchTags: [htmlSanitizedName],
      recid : Number(rawChartList.recID),
      listNum,
      charts: [],
      isAttemptingToLoadCharts: false,
      hasAttemptedToLoadCharts: false,
      hasFailedToLoadCharts: false,
      isReadonly: rawChartList.isReadOnly,
      isFrameworkChartList: /^\d{4}\s\s+<b>.*/.test(name)
    });
  }

  return chartLists;
}
